
import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../mainApi';

const localizer = momentLocalizer(moment);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/';
const ROOT_URL = API_BASE_URL.replace(/\/api\/?$/, '');

const Events = () => {
  const [events, setEvents] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [location, setLocation] = useState('');
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);


  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleFileChange = (e, type) => {
    if (type === 'image') {
      setImage(e.target.files[0]);
    } else if (type === 'video') {
      setVideo(e.target.files[0]);
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('event_date', eventDate);
    formData.append('event_time', eventTime);
    formData.append('location', location);
    if (image) formData.append('image', image);
    if (video) formData.append('video', video);

    try {
      const response = await api.post('events', formData);
      setEvents([...events, response.data]);
      setTitle('');
      setDescription('');
      setEventDate('');
      setEventTime('');
      setLocation('');
      setImage(null);
      setVideo(null);
    } catch (error) {
      console.error('Error adding event:', error);
      if (error.response) {
          alert(`Error: ${error.response.data.message}`);
      }
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`events/${id}`);
      setEvents(events.filter(event => event.id !== id));
    } catch (error) {
      console.error('Error deleting event:', error);
      if (error.response) {
          alert(`Error: ${error.response.data.message}`);
      }
    }
  };

  const handleShowModal = (event) => {
    setSelectedEvent({
      ...event,
      event_date: event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setImage(null); // Clear files when closing modal
    setVideo(null);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', selectedEvent.title);
    formData.append('description', selectedEvent.description);
    formData.append('event_date', selectedEvent.event_date);
    formData.append('event_time', selectedEvent.event_time);
    formData.append('location', selectedEvent.location);
    // Only append files if new ones are selected
    if (image) formData.append('image', image);
    if (video) formData.append('video', video);
    // Keep existing URLs if no new file is uploaded
    if (!image && selectedEvent.image_url) formData.append('image_url', selectedEvent.image_url);
    if (!video && selectedEvent.video_url) formData.append('video_url', selectedEvent.video_url);


    try {
      const response = await api.put(`events/${selectedEvent.id}`, formData);
      setEvents(events.map(event => event.id === response.data.id ? response.data : event));
      handleCloseModal();
    } catch (error) {
      console.error('Error updating event:', error);
      if (error.response) {
          alert(`Error: ${error.response.data.message}`);
      }
    }
  };

  const calendarEvents = events.map(event => ({
    id: event.id,
    title: event.title,
    start: new Date(event.event_date),
    end: new Date(event.event_date),
    allDay: true,
  }));

  return (
    <div>
      <h2>Events</h2>

      <form onSubmit={addEvent}>
        <div className="row gy-2 mb-3">
          <div className="col-md-3">
            <input type="text" className="form-control" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="col-md-3">
            <input type="text" className="form-control" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="col-md-3">
            <input type="date" className="form-control" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required />
          </div>
          <div className="col-md-3">
            <input type="time" className="form-control" value={eventTime} onChange={(e) => setEventTime(e.target.value)} />
          </div>
          <div className="col-md-3">
            <input type="text" className="form-control" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label htmlFor="imageUpload" className="form-label">Image</label>
            <input type="file" className="form-control" id="imageUpload" accept="image/*" onChange={(e) => handleFileChange(e, 'image')} />
          </div>
          <div className="col-md-3">
            <label htmlFor="videoUpload" className="form-label">Video</label>
            <input type="file" className="form-control" id="videoUpload" accept="video/*" onChange={(e) => handleFileChange(e, 'video')} />
          </div>
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary w-100">Add Event</button>
          </div>
        </div>
      </form>

      <hr />

      <div className="mb-3">
        <Button onClick={() => setViewMode('table')} variant={viewMode === 'table' ? 'primary' : 'secondary'}>Table View</Button>{' '}
        <Button onClick={() => setViewMode('calendar')} variant={viewMode === 'calendar' ? 'primary' : 'secondary'}>Calendar View</Button>
      </div>

      {viewMode === 'table' ? (
        <table className="table table-sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Date</th>
              <th>Time</th>
              <th>Location</th>
              <th>Image</th>
              <th>Video</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(event => (
              <tr key={event.id}>
                <td>{event.id}</td>
                <td>{event.title}</td>
                <td>{event.description}</td>
                <td>{event.event_date}</td>
                <td>{event.event_time}</td>
                <td>{event.location}</td>
                <td>
                  {event.image_url && <img src={`${ROOT_URL}${event.image_url}`} alt="Event" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />}
                </td>
                <td>
                  {event.video_url && <video src={`${ROOT_URL}${event.video_url}`} controls style={{ width: '50px', height: '50px', objectFit: 'cover' }} />}
                </td>
                <td>
                  <Button variant="warning" size="sm" onClick={() => handleShowModal(event)}>Edit</Button>{' '}
                  <Button variant="danger" size="sm" onClick={() => deleteEvent(event.id)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 500 }}
          onSelectEvent={event => handleShowModal(events.find(e => e.id === event.id))}
        />
      )}

      {selectedEvent && (
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Event</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleUpdateEvent}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input type="text" className="form-control" value={selectedEvent.title} onChange={(e) => setSelectedEvent({ ...selectedEvent, title: e.target.value })} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <input type="text" className="form-control" value={selectedEvent.description} onChange={(e) => setSelectedEvent({ ...selectedEvent, description: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Date</label>
                <input type="date" className="form-control" value={selectedEvent.event_date} onChange={(e) => setSelectedEvent({ ...selectedEvent, event_date: e.target.value })} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Time</label>
                <input type="time" className="form-control" value={selectedEvent.event_time} onChange={(e) => setSelectedEvent({ ...selectedEvent, event_time: e.target.value })} />
              </div>
              <div className="mb-3">
                <label className="form-label">Location</label>
                <input type="text" className="form-control" value={selectedEvent.location} onChange={(e) => setSelectedEvent({ ...selectedEvent, location: e.target.value })} />
              </div>
              <div className="mb-3">
                <label htmlFor="editImageUpload" className="form-label">Image</label>
                <input type="file" className="form-control" id="editImageUpload" accept="image/*" onChange={(e) => handleFileChange(e, 'image')} />
                {selectedEvent.image_url && !image && <img src={`${ROOT_URL}${selectedEvent.image_url}`} alt="Event" style={{ width: '50px', height: '50px', objectFit: 'cover', marginTop: '10px' }} />}
              </div>
              <div className="mb-3">
                <label htmlFor="editVideoUpload" className="form-label">Video</label>
                <input type="file" className="form-control" id="editVideoUpload" accept="video/*" onChange={(e) => handleFileChange(e, 'video')} />
                {selectedEvent.video_url && !video && <video src={`${ROOT_URL}${selectedEvent.video_url}`} controls style={{ width: '50px', height: '50px', objectFit: 'cover', marginTop: '10px' }} />}
              </div>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </form>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default Events;

